/* eslint-disable @typescript-eslint/no-unused-vars */

import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { LocalScanningWebApiService } from '../../../services/local-scanning-web-api.service';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MasterForm } from './master-forms.model';
import { ImageViewerComponent, Region } from './image/image-viewer.component';
import { PendingCollectionService } from '../../../services/pending-collection.service';
import { find } from 'lodash-es';
import { ConfirmClearAlertsDialogService } from './clear-dialog/confirm-clear-alerts-dialog.service';
import { AlertItem, Pending } from '../../biographics/pending.model';
import { SpinnerService, ThrottleClickDirective } from '@drc-eca/eca-lrc-common-ui';
import { MatButton } from '@angular/material/button';
import { AlertsReviewTableComponent } from './table/table.component';
import { AsyncPipe } from '@angular/common';
import { SubmitErrorComponent } from './submit-error/submit-error.component';


@Component({
  selector: 'eca-alerts-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, MatButton, RouterLink, ThrottleClickDirective, AlertsReviewTableComponent, ImageViewerComponent, SubmitErrorComponent, AsyncPipe]
})
export class AlertsReviewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private pendingCollection = inject(PendingCollectionService);
  private api = inject(LocalScanningWebApiService);
  private clearDialog = inject(ConfirmClearAlertsDialogService);
  private spinnerService = inject(SpinnerService);


  pendingId: string;
  sortKey: string;
  form: UntypedFormGroup;
  materialId: string;
  currentMasterForm: MasterForm;
  previousScanId: string;

  masterForm$ = new Subject<MasterForm>();
  regions$: Observable<Region[]>;
  alerts$: Observable<AlertItem[]>;
  hasOnlyBiographicAlerts$: Observable<boolean>;
  hasOnlySubmitAlerts$: Observable<boolean>;
  hasUnsubmittableAlerts$: Observable<boolean>;
  imageSrc$ = new BehaviorSubject<string>('');
  loadedImage$ = new Subject<boolean>();
  selectAlert$ = new Subject<AlertItem>();

  constructor() {
    this.form = new UntypedFormGroup({
      search: new UntypedFormControl(''),
    });
  }

  selectAlert(alert: AlertItem) {
    if (alert.alertType === 'biographic' || alert.alertType === 'submit') {
      return;
    }

    if (this.materialId !== alert.materialId) {
      this.materialId = alert.materialId;
      this.pendingCollection.getPending(this.pendingId).pipe(
        switchMap((pending: Pending) => this.api.getMasterForm(alert.materialId, pending)),
        take(1),
      ).subscribe((masterForm) => {
        this.currentMasterForm = masterForm;
        this.masterForm$.next(masterForm);
      });
    } else {
      this.masterForm$.next(this.currentMasterForm);
    }

    this.selectAlert$.next(alert);

    if (alert.scanId !== this.previousScanId) {
      this.spinnerService.show();
      this.api.getScanImageUrl(alert.scanId).subscribe((signedUrl) => {
        this.imageSrc$.next(signedUrl);
      });
    } else {
      this.loadedImage$.next(undefined);
    }

    this.previousScanId = alert.scanId;
  }

  imageLoaded() {
    this.spinnerService.hide();
    this.loadedImage$.next(undefined);
  }

  showConfirmation() {
    this.clearDialog.open()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          return this.clearAlerts();
        }),
      )
      .subscribe(
        () => {
          this.pendingCollection.clearAlertsForId(this.pendingId);
          this.router.navigateByUrl('/biographics');
        });
  }

  clearAlerts(): Observable<any> {
    return this.api.clearAlertFor({
      lithocode: this.pendingId
    });
  }

  ngOnInit() {
    const { pendingId } = this.route.snapshot.parent.params;
    this.pendingId = pendingId;
    this.alerts$ = this.pendingCollection.getAlertItems(pendingId);
    this.hasUnsubmittableAlerts$ = this.alerts$.pipe(
      map(alerts => !!find(alerts, alert => alert.alertType === 'biographic' || alert.alertType === 'scoreInput'))
    );
    this.hasOnlyBiographicAlerts$ = this.alerts$.pipe(
      map(alerts => alerts.length === 1 && alerts[0].alertType === 'biographic'),
    );
    this.hasOnlySubmitAlerts$ = this.alerts$.pipe(
      map(alerts => alerts.length === 1 && alerts[0].alertType === 'submit'),
    );

    this.regions$ = combineLatest([
      this.masterForm$,
      this.loadedImage$.asObservable().pipe(
        withLatestFrom(this.selectAlert$.asObservable()),
        map(([_, alert]) => alert)),
    ]).pipe(
      map(([masterForm, alert]) => {
        /* istanbul ignore if */
        if (!masterForm) {
          return [];
        }
        const page = masterForm.pages.find(p => p.pageNumber === alert.pageId);
        return page.inputs.filter(input => input.humanReadableId === alert.question);
      }),
    );
  }
}
